// The flag at the top right of the website header (Rithik, 29 Oct 2026: "a
// flag to indicate its uk ... usa flag when in the us"). It shows the
// visitor's country (detected, or their choice) and opens a country and
// language picker. Country sets the currency; language switches to that
// language's pages (/de/...). docs/international/CONTRACT.md section 1.
//
// Layout (1 Nov 2026, Rithik: "fix this ui"): the 24 language chips filled
// the panel, so only about three countries showed and the price note was cut
// off. Language is now one row that opens its list, the visitor's country is
// pinned and ticked at the top of the country list, the panel scrolls as one
// area with the price note always visible, and it opens under the flag.
// Every visible word is already in the translation catalogs ("Language",
// "Country", "Search countries", ...), so translated pages stay translated.
import { useEffect, useId, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  countryName,
  flagUrl,
  getCountry,
  isLiveLanguage,
  languageName,
  liveLanguages,
  sortedCountries,
} from "./countries.js";
import { setLocaleChoice, useLocaleState } from "./locale.js";
import { pageKind } from "./translate/boot.js";
import { pathForLanguage } from "./translate/routing.js";
import "./LocalePicker.css";

// Other names people type for a country ("uk" found only Ukraine, 1 Nov 2026 live check). In i18n/data, which the
// catalog extractor skips: search words, not text to translate.
import SEARCH_ALIASES from "./data/countrySearchAliases.json";

function aliasesOf(code) {
  return SEARCH_ALIASES[code] || [];
}

function languageForCountry(code, currentLanguage) {
  const country = getCountry(code);
  const languages = country?.languages || [];
  if (languages.includes(currentLanguage)) return currentLanguage;
  return languages.find((language) => isLiveLanguage(language)) || currentLanguage;
}

function goToLanguage(language) {
  if (typeof window === "undefined") return;
  const { pathname, search, hash } = window.location;
  const kind = pageKind(pathname);
  if (kind === "marketing" || kind === "localized") {
    const target = pathForLanguage(pathname, language) + search + hash;
    if (target !== pathname + search + hash) {
      window.location.assign(target);
      return true;
    }
  }
  return false;
}

// Where the panel opens: just under the flag, its right edge in line with the flag's.
function anchorFor(trigger) {
  const rect = trigger?.getBoundingClientRect?.();
  if (!rect || !(rect.width || rect.height) || typeof window === "undefined") return null;
  return {
    top: Math.max(8, Math.round(rect.bottom + 8)),
    right: Math.max(12, Math.round(window.innerWidth - rect.right)),
  };
}

function CheckIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true">
      <path d="M3.5 8.5 6.5 11.5 12.5 5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export default function LocalePicker({ placement = "header", className = "" }) {
  const state = useLocaleState();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [languagesOpen, setLanguagesOpen] = useState(false);
  const [anchor, setAnchor] = useState(null);
  const triggerRef = useRef(null);
  const panelRef = useRef(null);
  const searchRef = useRef(null);
  const titleId = useId();
  const languageLabelId = useId();
  const languageValueId = useId();
  const languageListId = useId();
  const countryLabelId = useId();
  const languages = liveLanguages();
  const countryLabel = countryName(state.country, state.language);
  const countries = useMemo(() => (open ? sortedCountries(state.language) : []), [open, state.language]);
  const searching = Boolean(query.trim());
  const filtered = useMemo(() => {
    const q = query.trim().toLocaleLowerCase();
    if (!q) return countries;
    return countries.filter(
      (country) =>
        country.displayName.toLocaleLowerCase().includes(q) ||
        country.name.toLocaleLowerCase().includes(q) ||
        country.code.toLowerCase() === q ||
        country.currency.toLowerCase() === q ||
        aliasesOf(country.code).some((alias) => alias.startsWith(q)),
    );
  }, [countries, query]);
  // The visitor's own country comes first (ticked) until they search; the rest follow in order.
  const current = searching ? null : countries.find((country) => country.code === state.country) || null;
  const others = current ? filtered.filter((country) => country.code !== current.code) : filtered;

  const openPanel = () => {
    setAnchor(anchorFor(triggerRef.current));
    setOpen(true);
  };

  const closePanel = () => {
    setOpen(false);
    setQuery("");
    setLanguagesOpen(false);
  };

  useEffect(() => {
    if (!open) return undefined;
    const dismiss = () => {
      setOpen(false);
      setQuery("");
      setLanguagesOpen(false);
    };
    const onKey = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        // Close the picker only: the phone menu it can be opened from closes on Escape too (a window listener).
        event.stopPropagation();
        dismiss();
        triggerRef.current?.focus();
      }
    };
    const onPointer = (event) => {
      if (panelRef.current?.contains(event.target) || triggerRef.current?.contains(event.target)) return;
      dismiss();
    };
    const onMove = () => setAnchor(anchorFor(triggerRef.current));
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onPointer);
    document.addEventListener("touchstart", onPointer, { passive: true });
    window.addEventListener("resize", onMove);
    window.addEventListener("scroll", onMove, { passive: true });
    // Type a country straight away with a mouse; on a phone, don't pop the keyboard over the list.
    const focusTimer = window.setTimeout(() => {
      const finePointer = typeof window.matchMedia === "function" && window.matchMedia("(pointer: fine)").matches;
      if (finePointer) searchRef.current?.focus({ preventScroll: true });
      else panelRef.current?.focus({ preventScroll: true });
    }, 30);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onPointer);
      document.removeEventListener("touchstart", onPointer);
      window.removeEventListener("resize", onMove);
      window.removeEventListener("scroll", onMove);
      window.clearTimeout(focusTimer);
    };
  }, [open]);

  const chooseCountry = (code) => {
    const language = languageForCountry(code, state.language);
    setLocaleChoice({ country: code, language });
    closePanel();
    if (language !== state.language) goToLanguage(language);
  };

  const chooseLanguage = (language) => {
    setLocaleChoice({ language });
    if (!goToLanguage(language)) closePanel();
  };

  // Enter in the search box picks the country it found: the only match, or the one whose name, code or other name is
  // exactly what was typed ("united kingdom", "GB", "uk"). Several loose matches ("united") wait for a click.
  const chooseSearchMatch = () => {
    const q = query.trim().toLocaleLowerCase();
    if (!q) return;
    const exact = filtered.find(
      (country) =>
        country.displayName.toLocaleLowerCase() === q ||
        country.name.toLocaleLowerCase() === q ||
        country.code.toLowerCase() === q ||
        aliasesOf(country.code).includes(q),
    );
    const match = exact || (filtered.length === 1 ? filtered[0] : null);
    if (match) chooseCountry(match.code);
  };

  const renderCountry = (country, pinned = false) => {
    const selected = country.code === state.country;
    return (
      <li
        key={(pinned ? "current-" : "") + country.code}
        role="option"
        aria-selected={selected}
        className={pinned ? "offlico-locale-list__current" : undefined}
      >
        <button type="button" className={"offlico-locale-option" + (selected ? " is-selected" : "")} onClick={() => chooseCountry(country.code)}>
          <img src={flagUrl(country.code)} alt="" width="22" height="15" loading={pinned ? "eager" : "lazy"} decoding="async" />
          <span className="offlico-locale-option__name" translate="no">{country.displayName}</span>
          <span className="offlico-locale-option__currency" translate="no">{country.currency}</span>
          <span className="offlico-locale-option__check">{selected ? <CheckIcon /> : null}</span>
        </button>
      </li>
    );
  };

  const panelStyle = anchor
    ? { "--offlico-locale-top": anchor.top + "px", "--offlico-locale-right": anchor.right + "px" }
    : undefined;

  const panel = open ? (
    <div className={"offlico-locale-layer offlico-locale-layer--" + placement}>
      <div
        ref={panelRef}
        className="offlico-locale-panel"
        role="dialog"
        aria-modal="false"
        aria-labelledby={titleId}
        tabIndex={-1}
        style={panelStyle}
      >
        <div className="offlico-locale-panel__header">
          <p id={titleId} className="offlico-locale-panel__title">Country and language</p>
          <button type="button" className="offlico-locale-panel__close" aria-label="Close" onClick={closePanel}>
            <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true"><path d="M4 4l8 8M12 4l-8 8" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" /></svg>
          </button>
        </div>
        <div className="offlico-locale-panel__body">
          {languages.length > 1 ? (
            <section className="offlico-locale-section" aria-labelledby={languageLabelId}>
              <p id={languageLabelId} className="offlico-locale-section__label">Language</p>
              <button
                type="button"
                className={"offlico-locale-select" + (languagesOpen ? " is-open" : "")}
                aria-expanded={languagesOpen}
                aria-controls={languageListId}
                aria-labelledby={languageLabelId + " " + languageValueId}
                onClick={() => setLanguagesOpen((value) => !value)}
              >
                <svg className="offlico-locale-select__icon" width="18" height="18" viewBox="0 0 20 20" aria-hidden="true">
                  <circle cx="10" cy="10" r="7.25" fill="none" stroke="currentColor" strokeWidth="1.5" />
                  <path d="M2.75 10h14.5M10 2.75c2.1 2.1 3.1 4.5 3.1 7.25S12.1 15.15 10 17.25M10 2.75C7.9 4.85 6.9 7.25 6.9 10s1 5.15 3.1 7.25" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                </svg>
                <span id={languageValueId} className="offlico-locale-select__value" lang={state.language} translate="no">
                  {languageName(state.language)}
                </span>
                <svg className="offlico-locale-select__chevron" width="12" height="12" viewBox="0 0 10 10" aria-hidden="true"><path d="M2 3.5 5 6.5 8 3.5" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
              </button>
              {languagesOpen ? (
                <div id={languageListId} className="offlico-locale-languages" role="group" aria-labelledby={languageLabelId}>
                  {languages.map((language) => {
                    const selected = language.code === state.language;
                    return (
                      <button
                        type="button"
                        key={language.code}
                        lang={language.code}
                        translate="no"
                        className={"offlico-locale-language" + (selected ? " is-selected" : "")}
                        aria-pressed={selected}
                        onClick={() => chooseLanguage(language.code)}
                      >
                        <span className="offlico-locale-language__name">{languageName(language.code)}</span>
                        {selected ? <CheckIcon /> : null}
                      </button>
                    );
                  })}
                </div>
              ) : null}
            </section>
          ) : null}
          <section className="offlico-locale-section offlico-locale-section--countries" aria-labelledby={countryLabelId}>
            <p id={countryLabelId} className="offlico-locale-section__label">Country</p>
            <label className="offlico-locale-search">
              <span className="offlico-locale-sr">Search countries</span>
              <svg className="offlico-locale-search__icon" width="16" height="16" viewBox="0 0 16 16" aria-hidden="true">
                <circle cx="7" cy="7" r="4.75" fill="none" stroke="currentColor" strokeWidth="1.6" />
                <path d="m10.5 10.5 3 3" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
              </svg>
              <input
                ref={searchRef}
                type="search"
                value={query}
                placeholder="Search countries"
                autoComplete="off"
                onChange={(event) => setQuery(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key !== "Enter") return;
                  event.preventDefault();
                  chooseSearchMatch();
                }}
              />
            </label>
            <ul className="offlico-locale-list" role="listbox" aria-labelledby={countryLabelId}>
              {current ? renderCountry(current, true) : null}
              {others.map((country) => renderCountry(country))}
              {!filtered.length ? <li className="offlico-locale-empty">No countries match</li> : null}
            </ul>
          </section>
        </div>
        {/* No translate="no" on the currency: the translator skips such a child and then translates the words around it
            one piece at a time ("Preise werden angezeigt in EUR."); as a plain tag the whole sentence matches its
            catalog entry "Prices are shown in <0>{0}</0>." ("Preise werden in EUR angezeigt."). */}
        <p className="offlico-locale-panel__note">
          Prices are shown in <span>{state.billingCurrency}</span>.
        </p>
      </div>
    </div>
  ) : null;

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        className={["offlico-locale-trigger", "offlico-locale-trigger--" + placement, className].filter(Boolean).join(" ")}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={`Country and language: ${countryLabel}, ${languageName(state.language)}`}
        title={countryLabel}
        onClick={() => (open ? closePanel() : openPanel())}
        data-cta-kind="locale"
      >
        <img className="offlico-locale-flag" src={flagUrl(state.country)} alt="" width="22" height="15" decoding="async" />
        {placement === "drawer" ? (
          <span className="offlico-locale-name" translate="no">{countryLabel + " · " + languageName(state.language)}</span>
        ) : languages.length > 1 ? (
          <span className="offlico-locale-code" translate="no">{state.language.toUpperCase()}</span>
        ) : null}
        <svg className="offlico-locale-chevron" width="10" height="10" viewBox="0 0 10 10" aria-hidden="true"><path d="M2 3.5 5 6.5 8 3.5" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
      </button>
      {panel && typeof document !== "undefined" ? createPortal(panel, document.body) : null}
    </>
  );
}
